import { t } from "@lingui/macro";
import {
  type BrandForNavigationQuery,
  useBrandForNavigationQuery,
} from "@src/__generated__/graphql";
import { useNavigationPages } from "@src/components/widgets/AppSubNav/hooks";
import type { NavigationHook } from "@src/components/widgets/AppSubNav/hooks/types";
import { cannot } from "@src/utils/components/permissions";
import { useStore } from "@src/utils/hooks";

export function useBrandNavigationPages(
  brandId: string,
): NavigationHook<BrandForNavigationQuery["brand"]> {
  const { workspaceStore } = useStore();
  const { data, loading } = useBrandForNavigationQuery({
    variables: { id: brandId },
    skip: !brandId,
    notifyOnNetworkStatusChange: true,
    fetchPolicy: "cache-first",
  });

  const { allPages, activePage, activeSubpage, visiblePages } =
    useNavigationPages([
      {
        href: { pathname: "/brands/detail", query: { id: brandId } },
        title: () => t`Detail`,
        hidden: cannot("brand_read"),
      },
      {
        href: { pathname: "/brands/documents", query: { id: brandId } },
        title: () => t`Documents`,
        hidden: cannot("brand_read"),
      },
      {
        href: { pathname: "/brands/credit-accounts", query: { id: brandId } },
        title: () => t`Credit accounts`,
        hidden: cannot("brand_read"),
      },
      {
        href: { pathname: "/brands/projects", query: { id: brandId } },
        title: () => t`Projects`,
        hidden: cannot("brand_read"),
      },
      {
        href: {
          pathname: "/brands/hourly-rates-and-commissions",
          query: { id: brandId },
        },
        title: () => t`Hourly rates and commissions`,
        hidden:
          cannot("brand_read") &&
          workspaceStore.settings?.moduleSettings.budgetovac,
      },
    ]);

  return {
    data: data?.brand,
    isLoading: loading,
    visiblePages,
    allPages,
    activePage,
    activeSubpage,
  };
}
